<script setup lang="ts">
import { computed } from 'vue'
import { NPopconfirm } from 'naive-ui'
import type { ReminderSchedule } from '../stores/reminders'

// One recurring schedule in the Reminders card: the message, how it
// recurs (with its end date and next firing underneath), the fire time in
// mono, and edit / delete.
const props = defineProps<{ schedule: ReminderSchedule }>()

const emit = defineEmits<{ edit: [id: number]; delete: [id: number] }>()

const CONFIRM_TRUNCATE_LENGTH = 50
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const recurrence = computed(() => {
  const s = props.schedule
  const kind = s.recurrence_type.charAt(0).toUpperCase() + s.recurrence_type.slice(1)
  const day = s.day_of_week !== null ? DAY_NAMES[s.day_of_week] : undefined
  return day ? `${kind} on ${day}s` : kind
})

const nextFire = computed(() => {
  const at = props.schedule.next_fire_at ? new Date(props.schedule.next_fire_at) : null
  if (!at || Number.isNaN(at.getTime())) return ''
  return at.toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
})

const truncated = computed(() => {
  const text = props.schedule.message
  return text.length > CONFIRM_TRUNCATE_LENGTH ? text.slice(0, CONFIRM_TRUNCATE_LENGTH) + '…' : text
})

const ACTION_CLASS =
  'min-h-11 rounded-lg border border-border-control px-2.5 text-xs text-ink-muted hover:text-ink sm:min-h-8'
</script>

<template>
  <div class="flex min-h-[52px] items-center gap-3 border-t border-border-divider py-2" data-testid="schedule-row">
    <span class="flex min-w-0 flex-1 flex-col gap-0.5">
      <span class="break-words text-ink">{{ schedule.message }}</span>
      <span class="text-xs text-ink-faint">
        <span class="text-life">{{ recurrence }}</span> · {{ schedule.timezone }}
        <span v-if="schedule.ends_on"> · ends {{ schedule.ends_on }}</span>
        <span v-if="nextFire"> · next {{ nextFire }}</span>
      </span>
    </span>
    <span class="shrink-0 font-mono text-xs text-ink-muted">{{ schedule.fire_at_time }}</span>
    <button
      type="button"
      :class="ACTION_CLASS"
      :aria-label="`Edit schedule: ${schedule.message}`"
      @click="emit('edit', schedule.id)"
    >
      Edit
    </button>
    <NPopconfirm positive-text="Delete" negative-text="Cancel" @positive-click="emit('delete', schedule.id)">
      <template #trigger>
        <button type="button" :class="ACTION_CLASS" :aria-label="`Delete schedule: ${schedule.message}`">Delete</button>
      </template>
      Delete schedule "{{ truncated }}" and its pending reminders?
    </NPopconfirm>
  </div>
</template>
